import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AnnotationDisplayOptions, DEFAULT_DISPLAY_OPTIONS } from "@/types/annotationDisplayOptions";
import {
  exportAnnotatedImageForEntry,
  downloadBlob,
  getOriginalImageBlob,
  getImageUrlAndMetadata,
} from "@/services/annotatedImageExportService";
import { getImageDownloadURL, getAnnotationMetadata, invalidateAnnotationMetadataCache } from "@/utils/entryUtils";
import {
  CheckCircle2,
  Download,
  Eye,
  EyeOff,
  Layers,
  Type,
  RemoveFormatting,
  Loader2,
  Pencil,
  RotateCcw,
  Save,
  ScanSearch,
} from "lucide-react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Timestamp } from "firebase/firestore";
import {
  annotationExtractor,
  KeyPoints,
  ParsedKeypointAnnotation,
  parseKeypointAnnotations,
  Segmentation,
  type SegmentationReviewHandle,
} from "./Annotations";
import type { ReviewLandingEdge, ReviewStepResult } from "@/utils/reviewObjectNavigation";
import {
  haveSegmentationAnnotationsChangedSinceLastSave,
  haveParsedKeypointAnnotationsChangedSinceLastSave,
} from "./Annotations/annotationChangeDetection";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { getAnnotationSaveErrorMessage, MISSING_ENTRY_PATH_ERROR } from "@/lib/firebaseErrors";
import type { Annotation } from "@/types/annotations";
import type { ContainmentParams } from "@shared/containmentOverlapFilter";
import type { MeasurementDescriptor } from "@shared/types/measurementDescriptors";
import type { AdditionalMeasurementAvgMetrics } from "@/utils/annotationMeasurementUtils";
import { computeVisibleAdditionalMeasurementAverages } from "@/utils/annotationMeasurementUtils";
import { useToast } from "@/hooks/use-toast";
import { ANNOTATION_TYPES, AnnotationType, Point } from "./Annotations/types";
import { countPerSquareMeterToPerAcre } from "@/lib/standCountUtils";
import {
  applySavedManualKeypointState,
  applySavedModelKeypointState,
  buildKeypointAnnotationMutationBatches,
  buildKeypointAnnotationSaveState,
  buildSegmentationAnnotationSaveState,
  resolveKeypointName,
} from "./Annotations/annotationSaveState";
import type { KeypointAnnotationSaveState, SegmentationAnnotationSaveState } from "./Annotations/annotationSaveState";
import {
  DEFAULT_ANNOTATION_VIEW_SETTINGS,
  type AnnotationViewSettings,
  type SegmentationFilterSettings,
} from "./Annotations/annotationViewSettings";
import { deriveMeasurementEditingMode } from "@/lib/measurement/depthAvailability";
import { useDepthAvailability } from "./Annotations/Segmentation/useDepthAvailability";
import { getDepthToggleLabel } from "./annotatedImageViewerLabels";
import { buildAnnotationClassNames } from "@shared/annotationClassColors";
import { getAnnotationsForSavedAggregates, hasVisibleAnnotationText } from "./Annotations/annotationClassInteractions";
import { isAnnotationEligibleForOutsideBoundaryReveal } from "./Annotations/outsideBoundaryAnnotations";
import {
  acknowledgeOutsideBoundarySaveWarning,
  hasAcknowledgedOutsideBoundarySaveWarning,
} from "./Annotations/outsideBoundaryWarningSession";

interface AnnotatedImageViewerProps {
  dataCollectionOnly?: boolean;
  /** Firebase Storage path to the original image */
  imageUrl: string;
  /** Firebase Storage path to the annotation metadata JSON (optional) */
  metadataPath?: string;
  /** Upload status (not used for cache invalidation) */
  uploadStatus?: string;
  /** Timestamp for cache invalidation on re-uploads */
  updatedAt?: Timestamp;
  /** Filename to use when downloading */
  downloadFilename?: string;
  /** Callback when image is closed (optional) */
  onClose?: () => void;
  /** Current display options */
  displayOptions?: AnnotationDisplayOptions;
  /** Callback when display options change (optional) */
  onDisplayOptionsChange?: (options: AnnotationDisplayOptions) => void;
  /** Called after annotations are successfully saved */
  onSave?: (metrics: {
    countPerSquareMeter?: number;
    countPerAcre?: number;
    objectCount?: number;
    avgLengthMeters?: number | null;
    avgWidthMeters?: number | null;
    totalWeightGrams?: number | null;
    additionalMeasurements?: AdditionalMeasurementAvgMetrics;
    userDidOverride?: boolean;
  }) => Promise<void> | void;
  /** Called when a deferred analysis result is reviewed without annotation changes */
  onMarkReviewed?: () => Promise<void> | void;
  /** Called when a reviewed deferred analysis result should be flagged for review again */
  onMarkUnreviewed?: () => Promise<void> | void;
  /** Whether this image has an unreviewed deferred analysis result */
  isReviewableUnreviewed?: boolean;
  /** Whether this image has a reviewed deferred analysis result */
  isReviewableReviewed?: boolean;
  /** Called when edit mode is toggled */
  onEditModeChange?: (isEditMode: boolean) => void;
  /** Whether the viewer should currently be in preview or edit mode */
  viewerMode?: "preview" | "edit";
  /** Called when unsaved-changes state changes */
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  /** Whether the current user can edit annotations (default true) */
  canEdit?: boolean;
  /** Whether the current user can mark deferred analysis review state (default follows canEdit) */
  canReview?: boolean;
  /** Whether this entry type supports annotation editing (default false) */
  isEditable?: boolean;
  /** Supported only by image-measurement and stand-count traits with a persisted boundary. */
  supportsOutsideBoundaryAnnotations?: boolean;
  /** Firestore path to the entry collection (for collector ownership verification in CF) */
  entryPath?: string;
  /**
   * The entry's own depth file path: `null` when the entry was consulted and has
   * none, `undefined` when it was never plumbed through. Only a consulted-and-empty
   * entry can unlock 2D landmark editing.
   */
  entryDepthCloudPath?: string | null;
  /** Preferred editor mode when metadata alone is ambiguous */
  annotationTypePreference?: AnnotationType;
  /** Containment overlap-filter params for this capture's trait (GLE-79);
   *  undefined = the trait did not opt in. Passed straight to the segmentation
   *  viewer, so it must be referentially stable across renders. */
  containmentParams?: ContainmentParams;
  /** Full descriptor set for labels, editable axes, and aggregate writes. */
  measurementDescriptors?: MeasurementDescriptor[];
  /**
   * View settings to restore on mount. `showMask` / `hiddenMeasurementKeys` are pure
   * screen state and never reach downloads/exports. The restored filters and keypoint
   * sensitivity are editorial: like a manual slider adjustment, they write visibility
   * into the annotation list, so they bake into the saved data through the normal
   * edit-mode save flow. Preview stays view-only — it applies the filter on screen but
   * never writes annotation data (so a filter inherited and only previewed is not saved).
   */
  initialViewSettings?: AnnotationViewSettings | null;
  /** Called with a fresh snapshot whenever a tracked on-screen view setting changes. */
  onViewSettingsChange?: (settings: AnnotationViewSettings) => void;
  /**
   * Which end of this image's object list review mode should open on. "last" when the reviewer
   * arrived by stepping backward off the front of the previous image.
   */
  initialReviewObjectEdge?: ReviewLandingEdge;
  /** Exact class names configured across the current annotation task. */
  annotationClassNames?: string[];
  /** Task-wide class order used to keep shared names on the same color across traits. */
  annotationClassColorOrder?: string[];
  /** Stable task key for retaining view-only class controls while navigating images. */
  annotationClassStateKey?: string;
}

const computeCountPerSquareMeter = (visibleCount: number, boundaryArea?: number): number | undefined =>
  boundaryArea && boundaryArea > 0 ? Math.round((visibleCount / boundaryArea) * 100) / 100 : undefined;
const EMPTY_MEASUREMENT_DESCRIPTORS: MeasurementDescriptor[] = [];
const EMPTY_ANNOTATION_CLASS_NAMES: string[] = [];

/** Toolbar copy for the review counter. A negative index means the reviewed object left the view. */
function describeReviewStatus({ index, total }: { index: number; total: number }): string {
  if (total === 0) return "No objects to review";
  if (index < 0) return "Object removed — ← → to continue";
  return `Object ${index + 1} of ${total}`;
}

export type AnnotatedImageViewerSaveStatus = "saved" | "no_changes" | "error" | "new_changes";

export interface AnnotatedImageViewerHandle {
  /** Ask the reviewer to confirm newly accepted outside-boundary annotations before any save begins. */
  confirmOutsideBoundaryAnnotationsBeforeSave: () => Promise<boolean>;
  saveIfThereAreUnsavedChanges: () => Promise<AnnotatedImageViewerSaveStatus>;
  canDiscardUnsavedChanges: () => boolean;
  discardUnsavedChanges: () => boolean;
  /**
   * Move the review cursor one object, reporting whether the key was consumed. "inactive" whenever
   * review mode can't act (off, preview, annotations hidden, or not a segmentation image), which
   * is the arrow-key owner's cue to fall through to normal image navigation.
   */
  stepReviewObject: (direction: "prev" | "next") => ReviewStepResult;
}

/**
 * A zoomable image viewer with annotation toggle controls.
 * Supports:
 * - Default to annotated view when metadata is available
 * - Icon+text toggles for annotations and text
 * - Scroll wheel zoom
 * - Drag to pan when zoomed
 * - Double-click to reset zoom
 * - Download current view
 */
export const AnnotatedImageViewer = React.forwardRef<AnnotatedImageViewerHandle, AnnotatedImageViewerProps>(
  (
    {
      imageUrl,
      dataCollectionOnly = false,
      metadataPath,
      uploadStatus,
      updatedAt,
      downloadFilename = "image.jpg",
      displayOptions: externalDisplayOptions,
      onDisplayOptionsChange,
      onSave,
      onMarkReviewed,
      onMarkUnreviewed,
      isReviewableUnreviewed = false,
      isReviewableReviewed = false,
      onEditModeChange,
      viewerMode = "preview",
      onUnsavedChangesChange,
      canEdit = true,
      canReview = canEdit,
      isEditable = false,
      supportsOutsideBoundaryAnnotations = false,
      entryPath,
      entryDepthCloudPath,
      annotationTypePreference,
      containmentParams,
      measurementDescriptors = [],
      initialViewSettings,
      onViewSettingsChange,
      initialReviewObjectEdge = "first",
      annotationClassNames = EMPTY_ANNOTATION_CLASS_NAMES,
      annotationClassColorOrder = EMPTY_ANNOTATION_CLASS_NAMES,
      annotationClassStateKey,
    },
    ref
  ) => {
    const { toast } = useToast();
    const requestedMetadataContextKey = JSON.stringify([imageUrl, metadataPath ?? null]);
    const requestedEditorContextKey = JSON.stringify([imageUrl, metadataPath ?? null, entryPath ?? null]);
    const requestedMetadataContextKeyRef = useRef(requestedMetadataContextKey);
    requestedMetadataContextKeyRef.current = requestedMetadataContextKey;
    const [metadataSnapshot, setMetadataSnapshot] = useState<{
      contextKey: string;
      value: Awaited<ReturnType<typeof getImageUrlAndMetadata>>;
    } | null>(null);
    const [metadataLoadFailedContextKey, setMetadataLoadFailedContextKey] = useState<string | null>(null);
    const isMetadataSnapshotCurrent = metadataSnapshot?.contextKey === requestedMetadataContextKey;
    const metadata = isMetadataSnapshotCurrent ? metadataSnapshot.value : undefined;
    const metadataLoadFailed = metadataLoadFailedContextKey === requestedMetadataContextKey;
    // Bumped by the depth retry so the metadata fetch above runs again after its
    // cache entry has been invalidated.
    const [metadataReloadNonce, setMetadataReloadNonce] = useState(0);
    const isEditMode = !dataCollectionOnly && viewerMode === "edit";

    // Display options state - use external if provided, otherwise internal
    const [internalDisplayOptions, setInternalDisplayOptions] = useState<AnnotationDisplayOptions>(() => ({
      ...DEFAULT_DISPLAY_OPTIONS,
      // Default to showing annotations if metadata is available
      showAnnotations: !!metadataPath,
    }));

    const displayOptions = externalDisplayOptions ?? internalDisplayOptions;
    const hasAnnotations = !dataCollectionOnly && !!metadataPath;
    const setDisplayOptions = onDisplayOptionsChange ?? setInternalDisplayOptions;
    const displayOptionsRef = useRef(displayOptions);

    useEffect(() => {
      displayOptionsRef.current = displayOptions;
    }, [displayOptions]);

    // Cache for annotated images - keyed by showText setting
    // This allows instant toggling between text/no-text without re-rendering
    const [annotatedWithTextUrl, setAnnotatedWithTextUrl] = useState<string | null>(null);
    const [annotatedWithoutTextUrl, setAnnotatedWithoutTextUrl] = useState<string | null>(null);
    const [isLoadingAnnotated, setIsLoadingAnnotated] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Preloaded original image URL - prevents glitch when toggling annotations off
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
    const [isLoadingOriginal, setIsLoadingOriginal] = useState(true);

    // Track whether the annotation has text labels available (measurements)
    const [hasTextLabels, setHasTextLabels] = useState(false);

    // Refs to track URLs for cleanup (avoids stale closure issues)
    const urlsRef = useRef<{ withText: string | null; withoutText: string | null }>({
      withText: null,
      withoutText: null,
    });

    // Zoom/pan state
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(true);

    // Get the current annotated image URL based on showText setting
    const annotatedImageUrl = displayOptions.showText ? annotatedWithTextUrl : annotatedWithoutTextUrl;

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Clear cached URLs when image source changes (P1 fix)
    useEffect(() => {
      let cancelled = false;
      const requestContextKey = requestedMetadataContextKey;

      setMetadataLoadFailedContextKey((failedContextKey) =>
        failedContextKey === requestContextKey ? null : failedContextKey
      );
      if (hasAnnotations) {
        getImageUrlAndMetadata(imageUrl, metadataPath, updatedAt)
          .then((result) => {
            if (!cancelled && isMountedRef.current && requestedMetadataContextKeyRef.current === requestContextKey) {
              setMetadataSnapshot({ contextKey: requestContextKey, value: result });
            }
          })
          .catch((error) => {
            if (!cancelled && requestedMetadataContextKeyRef.current === requestContextKey) {
              console.error("Failed to load annotation metadata:", error);
              // Surfaced so the depth decision can tell "metadata still loading"
              // apart from "metadata could not be read" (retryable).
              setMetadataLoadFailedContextKey(requestContextKey);
            }
          });
      } else {
        setMetadataSnapshot(null);
      }

      // Revoke old URLs when switching images
      if (urlsRef.current.withText) {
        URL.revokeObjectURL(urlsRef.current.withText);
      }
      if (urlsRef.current.withoutText) {
        URL.revokeObjectURL(urlsRef.current.withoutText);
      }
      urlsRef.current = { withText: null, withoutText: null };
      setAnnotatedWithTextUrl(null);
      setAnnotatedWithoutTextUrl(null);
      setHasTextLabels(false);

      return () => {
        cancelled = true;
      };
    }, [imageUrl, metadataPath, hasAnnotations, metadataReloadNonce, requestedMetadataContextKey, updatedAt]);

    // Check if metadata contains measurement labels (text to show/hide)
    useEffect(() => {
      if (!hasAnnotations) {
        setHasTextLabels(false);
        return;
      }

      let cancelled = false;

      const checkForTextLabels = async () => {
        try {
          const metadata = await getAnnotationMetadata(metadataPath, updatedAt);
          if (cancelled) return;

          if (!metadata?.annotations?.annotations) {
            setHasTextLabels(false);
            return;
          }

          setHasTextLabels(hasVisibleAnnotationText(metadata.annotations.annotations));
        } catch (error) {
          console.error("Failed to check for text labels:", error);
          setHasTextLabels(false);
        }
      };

      checkForTextLabels();

      return () => {
        cancelled = true;
      };
      // metadataReloadNonce: a depth/metadata Retry refetches the capture, and the
      // label detection must re-run against the refreshed metadata too.
    }, [metadataPath, hasAnnotations, metadataReloadNonce, updatedAt]);

    const extractedAnnotations = useMemo(() => {
      if (!metadata || !metadata.metadata.annotations) return null;
      return annotationExtractor(metadata.metadata.annotations, annotationTypePreference);
    }, [annotationTypePreference, metadata]);
    const resolvedAnnotationClassNames = useMemo(
      () =>
        buildAnnotationClassNames(
          annotationClassNames,
          metadata?.metadata.annotations?.annotations.map((annotation) => annotation.category?.name ?? "")
        ),
      [annotationClassNames, metadata]
    );

    const usesInteractiveAnnotationViewer =
      extractedAnnotations?.annotationType === ANNOTATION_TYPES.KEY_POINTS ||
      extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION;

    const [showDepthDebug, setShowDepthDebug] = useState(false);
    const isSegmentation = extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION;
    const additionalMeasurementDescriptors = useMemo(
      () => measurementDescriptors.filter((descriptor) => descriptor.binding === null),
      [measurementDescriptors]
    );
    useEffect(() => {
      if (!isEditMode) setShowDepthDebug(false);
    }, [imageUrl, isEditMode]);
    // What this capture's depth evidence allows the measurement editor to do:
    // recompute in 3D, move landmarks in 2D, or nothing at all.
    const reloadCaptureMetadata = useCallback(() => {
      // Drop the cached failure so the re-run actually hits Storage again.
      invalidateAnnotationMetadataCache(metadataPath);
      setMetadataReloadNonce((nonce) => nonce + 1);
    }, [metadataPath]);
    const { availability: depthAvailability, retry: handleRetryDepthLoad } = useDepthAvailability({
      enabled: isEditMode && isSegmentation,
      captureMetadata: metadata?.metadata ?? null,
      metadataFailed: metadataLoadFailed,
      metadataPath,
      entryDepthCloudPath,
      updatedAt,
      onMetadataInvalidated: reloadCaptureMetadata,
    });
    const measurementEditing = useMemo(() => deriveMeasurementEditingMode(depthAvailability), [depthAvailability]);

    // Generate annotated image when needed
    useEffect(() => {
      if (!displayOptions.showAnnotations || !imageUrl || !hasAnnotations || usesInteractiveAnnotationViewer) {
        return;
      }

      // Check which version we need
      const needsWithText = displayOptions.showText && !annotatedWithTextUrl;
      const needsWithoutText = !displayOptions.showText && !annotatedWithoutTextUrl;

      if (!needsWithText && !needsWithoutText) {
        // Both versions already cached, no need to generate
        return;
      }

      let cancelled = false;

      const generateAnnotatedImage = async () => {
        setIsLoadingAnnotated(true);
        try {
          const blob = await exportAnnotatedImageForEntry(
            imageUrl,
            metadataPath,
            {
              showLength: true,
              showWidth: true,
              showMass: true,
              showKeypoints: true,
              showSkeleton: true,
              showMeasureBoundary: true,
              showText: displayOptions.showText,
              annotationClassNames: resolvedAnnotationClassNames,
              annotationClassColorOrder,
            },
            updatedAt
          );

          // Create URL before checking cancelled so we can revoke it if needed
          const url = URL.createObjectURL(blob);

          if (cancelled) {
            // If cancelled, revoke the URL we just created and exit
            URL.revokeObjectURL(url);
            return;
          }

          if (displayOptions.showText) {
            // Revoke old URL before replacing (P2 fix)
            if (urlsRef.current.withText) {
              URL.revokeObjectURL(urlsRef.current.withText);
            }
            urlsRef.current.withText = url;
            setAnnotatedWithTextUrl(url);
          } else {
            // Revoke old URL before replacing (P2 fix)
            if (urlsRef.current.withoutText) {
              URL.revokeObjectURL(urlsRef.current.withoutText);
            }
            urlsRef.current.withoutText = url;
            setAnnotatedWithoutTextUrl(url);
          }
        } catch (error) {
          console.error("Failed to generate annotated image:", error);
          if (!cancelled) {
            // Fall back to showing original
            setDisplayOptions({ ...displayOptionsRef.current, showAnnotations: false });
          }
        } finally {
          if (!cancelled) {
            setIsLoadingAnnotated(false);
          }
        }
      };

      generateAnnotatedImage();

      return () => {
        cancelled = true;
      };
    }, [
      displayOptions.showAnnotations,
      displayOptions.showText,
      imageUrl,
      metadataPath,
      hasAnnotations,
      annotatedWithTextUrl,
      annotatedWithoutTextUrl,
      setDisplayOptions,
      updatedAt,
      usesInteractiveAnnotationViewer,
      resolvedAnnotationClassNames,
      annotationClassColorOrder,
    ]);

    // Cleanup blob URLs on unmount (P2 fix - uses ref to avoid stale closure)
    useEffect(() => {
      return () => {
        if (urlsRef.current.withText) {
          URL.revokeObjectURL(urlsRef.current.withText);
        }
        if (urlsRef.current.withoutText) {
          URL.revokeObjectURL(urlsRef.current.withoutText);
        }
      };
    }, []);

    // Preload original image URL to prevent glitch when toggling annotations off
    useEffect(() => {
      let cancelled = false;

      const loadOriginalUrl = async () => {
        setIsLoadingOriginal(true);
        try {
          const url = await getImageDownloadURL(imageUrl, uploadStatus, updatedAt);
          if (!cancelled && url) {
            setOriginalImageUrl(url);
          }
        } catch (error) {
          console.error("Failed to load original image URL:", error);
        } finally {
          if (!cancelled) {
            setIsLoadingOriginal(false);
          }
        }
      };

      loadOriginalUrl();

      return () => {
        cancelled = true;
      };
    }, [imageUrl, uploadStatus, updatedAt]);

    // Handle wheel zoom
    const handleWheel = useCallback(
      (e: React.WheelEvent) => {
        e.preventDefault();

        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newScale = Math.min(Math.max(scale + delta, 1), 5);

        if (newScale === 1) {
          // Reset position when zooming back to 1
          setPosition({ x: 0, y: 0 });
        }

        setScale(newScale);
      },
      [scale]
    );

    // Handle drag start
    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (scale <= 1) return;

        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      },
      [scale, position]
    );

    // Handle drag move
    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!isDragging || scale <= 1) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Constrain to reasonable bounds
        const maxOffset = (scale - 1) * 200;
        setPosition({
          x: Math.min(Math.max(newX, -maxOffset), maxOffset),
          y: Math.min(Math.max(newY, -maxOffset), maxOffset),
        });
      },
      [isDragging, scale, dragStart]
    );

    // Handle drag end
    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Handle double-click to reset zoom
    const handleDoubleClick = useCallback(() => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }, []);

    // Handle download - downloads EXACTLY what the user sees (WYSIWYG)
    const handleDownload = async () => {
      setIsDownloading(true);
      try {
        let blob: Blob;

        if (displayOptions.showAnnotations && hasAnnotations) {
          // Download annotated image with current display options
          blob = await exportAnnotatedImageForEntry(
            imageUrl,
            metadataPath,
            {
              showLength: true,
              showWidth: true,
              showMass: true,
              showKeypoints: true,
              showSkeleton: true,
              showMeasureBoundary: true,
              showText: displayOptions.showText,
              annotationClassNames: resolvedAnnotationClassNames,
              annotationClassColorOrder,
            },
            updatedAt
          );
        } else {
          // Download original image using Firebase Storage path
          blob = await getOriginalImageBlob(imageUrl);
        }

        downloadBlob(blob, downloadFilename);
      } catch (error) {
        console.error("Failed to download image:", error);
        alert("Failed to download image. Please try again.");
      } finally {
        setIsDownloading(false);
      }
    };

    // Toggle annotations
    const toggleAnnotations = () => {
      setDisplayOptions({
        ...displayOptions,
        showAnnotations: !displayOptions.showAnnotations,
      });
    };

    // Toggle text
    const toggleText = () => {
      setDisplayOptions({
        ...displayOptions,
        showText: !displayOptions.showText,
      });
    };

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [parsedKeypointAnnotations, setKeypointAnnotations] = useState<ParsedKeypointAnnotation[]>([]);
    const [segmentationAnnotations, setSegmentationAnnotations] = useState<Annotation[]>([]);
    const latestKeypointAnnotationsRef = useRef<ParsedKeypointAnnotation[]>([]);
    const latestSegmentationAnnotationsRef = useRef<Annotation[]>([]);
    const [annotationEditorContextKey, setAnnotationEditorContextKey] = useState<string | null>(null);
    const isAnnotationEditorCurrent =
      isMetadataSnapshotCurrent && annotationEditorContextKey === requestedEditorContextKey;
    // View-only: hide the mask fill while keeping keypoints/lines. Never affects downloads.
    const [showMask, setShowMask] = useState(initialViewSettings?.showMask ?? true);
    // Edit-only declutter: collapse every box to a center point (persisted like showMask).
    const [collapseBoxesToPoints, setCollapseBoxesToPoints] = useState(
      initialViewSettings?.collapseBoxesToPoints ?? false
    );
    // Edit-only sweep: zoom to one object at a time (persisted like collapseBoxesToPoints). The
    // cursor itself lives in Segmentation, which owns the filtered object list; this is just the
    // on/off flag the toolbar button drives, plus the counter the child reports back.
    const [reviewObjectsMode, setReviewObjectsMode] = useState(initialViewSettings?.reviewObjectsMode ?? false);
    const [showOutsideBoundaryAnnotations, setShowOutsideBoundaryAnnotations] = useState(
      initialViewSettings?.showOutsideBoundaryAnnotations ?? false
    );
    const [reviewStatus, setReviewStatus] = useState<{ index: number; total: number } | null>(null);
    const segmentationRef = useRef<SegmentationReviewHandle>(null);
    // The whole on-screen view-settings snapshot lives in one ref (not state) so
    // composing and reporting it never triggers a viewer re-render loop. Seeded from
    // the restore snapshot; each child callback patches its own slice.
    const viewSettingsRef = useRef<AnnotationViewSettings>({
      ...DEFAULT_ANNOTATION_VIEW_SETTINGS,
      ...(initialViewSettings ?? {}),
    });
    // Hold the reporter in a ref so patchViewSettings is identity-stable and the child
    // callbacks below never change identity as the parent re-renders.
    const onViewSettingsChangeRef = useRef(onViewSettingsChange);
    useEffect(() => {
      onViewSettingsChangeRef.current = onViewSettingsChange;
    }, [onViewSettingsChange]);

    const patchViewSettings = useCallback((partial: Partial<AnnotationViewSettings>) => {
      viewSettingsRef.current = { ...viewSettingsRef.current, ...partial };
      onViewSettingsChangeRef.current?.(viewSettingsRef.current);
    }, []);

    // On mount, report the seeded snapshot so the parent's latest-settings ref tracks
    // THIS viewer instance. Without it, tweaking a setting, closing the viewer, and
    // opening a different image would leave the parent holding the previous image's
    // stale snapshot until the next user change (the persist dialog listed it).
    useEffect(() => {
      onViewSettingsChangeRef.current?.(viewSettingsRef.current);
    }, []);

    const handleShowMaskChange = useCallback(
      (value: boolean) => {
        setShowMask(value);
        patchViewSettings({ showMask: value });
      },
      [patchViewSettings]
    );

    const handleCollapseBoxesToPointsChange = useCallback(
      (value: boolean) => {
        setCollapseBoxesToPoints(value);
        patchViewSettings({ collapseBoxesToPoints: value });
      },
      [patchViewSettings]
    );

    const handleReviewObjectsModeChange = useCallback(
      (value: boolean) => {
        setReviewObjectsMode(value);
        patchViewSettings({ reviewObjectsMode: value });
      },
      [patchViewSettings]
    );

    const handleShowOutsideBoundaryAnnotationsChange = useCallback(
      (value: boolean) => {
        setShowOutsideBoundaryAnnotations(value);
        patchViewSettings({ showOutsideBoundaryAnnotations: value });
      },
      [patchViewSettings]
    );

    const handleExitReviewMode = useCallback(
      () => handleReviewObjectsModeChange(false),
      [handleReviewObjectsModeChange]
    );

    // Bail out of equal updates: the child re-reports on every list/filter change, and a fresh
    // object each time would re-render the whole viewer for nothing.
    const handleReviewStatusChange = useCallback((status: { index: number; total: number } | null) => {
      setReviewStatus((previous) =>
        previous?.index === status?.index && previous?.total === status?.total ? previous : status
      );
    }, []);

    const handleFilterSettingsChange = useCallback(
      (settings: SegmentationFilterSettings) => patchViewSettings({ segmentationFilters: settings }),
      [patchViewSettings]
    );

    const handleHiddenMeasurementKeysChange = useCallback(
      (keys: string[]) => patchViewSettings({ hiddenMeasurementKeys: keys }),
      [patchViewSettings]
    );

    const handleKeypointConfidenceThresholdChange = useCallback(
      (threshold: number) => patchViewSettings({ keypointConfidenceThreshold: threshold }),
      [patchViewSettings]
    );

    const handleKeypointSizeChange = useCallback(
      (percent: number) => patchViewSettings({ keypointSizePercent: percent }),
      [patchViewSettings]
    );

    const isEditModeRef = useRef(false);
    const setUnsavedChangesState = useCallback(
      (hasUnsavedChangesNow: boolean) => {
        setHasUnsavedChanges(hasUnsavedChangesNow);
        onUnsavedChangesChange?.(hasUnsavedChangesNow);
      },
      [onUnsavedChangesChange]
    );
    const annotationChangesRef = useRef(false);
    const annotationChangeVersionRef = useRef(0);
    // Storage sidecars and Firestore aggregates are separate writes. Once the
    // sidecar succeeds, keep the pre-save editor snapshot authoritative until
    // the aggregate write also succeeds, so a metadata refetch cannot hide a
    // retryable split-write failure.
    const hasSidecarWriteAwaitingAggregateRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [outsideBoundarySaveConfirmationCount, setOutsideBoundarySaveConfirmationCount] = useState<number | null>(
      null
    );
    const outsideBoundarySaveConfirmationResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
    const resolveOutsideBoundarySaveConfirmation = useCallback((confirmed: boolean) => {
      if (confirmed) acknowledgeOutsideBoundarySaveWarning();
      const resolve = outsideBoundarySaveConfirmationResolverRef.current;
      outsideBoundarySaveConfirmationResolverRef.current = null;
      setOutsideBoundarySaveConfirmationCount(null);
      resolve?.(confirmed);
    }, []);
    const syncUnsavedChangesState = useCallback(() => {
      setUnsavedChangesState(annotationChangesRef.current || hasSidecarWriteAwaitingAggregateRef.current);
    }, [setUnsavedChangesState]);
    const resetUnsavedChangeSources = useCallback(() => {
      annotationChangesRef.current = false;
      hasSidecarWriteAwaitingAggregateRef.current = false;
      resolveOutsideBoundarySaveConfirmation(false);
      setUnsavedChangesState(false);
    }, [resolveOutsideBoundarySaveConfirmation, setUnsavedChangesState]);
    const setViewerEditMode = useCallback(
      (shouldUseEditMode: boolean) => {
        onEditModeChange?.(shouldUseEditMode);
        if (!shouldUseEditMode) {
          resetUnsavedChangeSources();
        }
      },
      [onEditModeChange, resetUnsavedChangeSources]
    );

    // Layout effect (not passive): all layout effects across the committed tree run
    // before any passive effect, so this ref is fresh before the child restore
    // write-backs (passive effects in Segmentation/KeyPoints) read it via
    // updateSegmentationAnnotations/updateKeypointAnnotations. React runs child passive
    // effects before parent passive effects, so a passive sync here would still read
    // stale `false` on the preview→edit flip, leaving the inherited filter unmarked and
    // Save returning no_changes (GLE round-5 P1).
    useLayoutEffect(() => {
      isEditModeRef.current = isEditMode;
    }, [isEditMode]);

    useLayoutEffect(() => {
      resetUnsavedChangeSources();
      setIsSaving(false);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }, [imageUrl, metadataPath, resetUnsavedChangeSources]);

    useEffect(() => {
      // A refetch must not clear the dirty flag before the metadata-sync effect
      // gets a chance to protect the working annotation set. A successful save or
      // image change clears the edit sources explicitly.
      setIsSaving(false);
    }, [updatedAt]);

    const savedAnnotationsRef = useRef<Annotation[] | null>(null);
    // This viewer is reused while stepping between images. Clear the optimistic
    // snapshot before child passive effects for the next image can compare
    // against it. updatedAt is intentionally excluded: an entry timestamp may
    // advance after this component's own save while its metadata prop is stale.
    useLayoutEffect(() => {
      savedAnnotationsRef.current = null;
    }, [imageUrl, metadataPath]);
    const getLastSavedAnnotations = useCallback(
      () =>
        savedAnnotationsRef.current !== null
          ? savedAnnotationsRef.current
          : (metadata?.metadata.annotations?.annotations ?? []),
      [metadata]
    );

    const updateKeypointAnnotations = useCallback(
      (annotations: ParsedKeypointAnnotation[]) => {
        annotationChangeVersionRef.current += 1;
        latestKeypointAnnotationsRef.current = annotations;
        setKeypointAnnotations(annotations);
        if (!isEditModeRef.current) return;
        annotationChangesRef.current = haveParsedKeypointAnnotationsChangedSinceLastSave(
          annotations,
          getLastSavedAnnotations()
        );
        syncUnsavedChangesState();
      },
      [getLastSavedAnnotations, syncUnsavedChangesState]
    );

    const updateSegmentationAnnotations = useCallback(
      (annotations: Annotation[]) => {
        annotationChangeVersionRef.current += 1;
        latestSegmentationAnnotationsRef.current = annotations;
        setSegmentationAnnotations(annotations);
        if (!isEditModeRef.current) return;
        annotationChangesRef.current = haveSegmentationAnnotationsChangedSinceLastSave(
          annotations,
          getLastSavedAnnotations()
        );
        syncUnsavedChangesState();
      },
      [getLastSavedAnnotations, syncUnsavedChangesState]
    );

    // Warn on page refresh/close when there are unsaved changes
    useEffect(() => {
      if (!hasUnsavedChanges) return;
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
      };
      window.addEventListener("beforeunload", handler);
      return () => window.removeEventListener("beforeunload", handler);
    }, [hasUnsavedChanges]);

    // Derived from the very list handed to <Segmentation>, so the toolbar gate can't drift from
    // the child's own `hasBoxes`.
    const hasBoundingBoxes = useMemo(
      () => segmentationAnnotations.some((annotation) => annotation.bbox),
      [segmentationAnnotations]
    );
    const showingAnnotations = displayOptions.showAnnotations && hasAnnotations;

    // Check if we're loading - annotated when showing annotations, original when not
    const isLoading = showingAnnotations
      ? isLoadingAnnotated && !annotatedImageUrl
      : isLoadingOriginal && !originalImageUrl;

    const canMarkReviewed = isReviewableUnreviewed && canReview && !!onMarkReviewed;
    const canMarkUnreviewed = isReviewableReviewed && canReview && !!onMarkUnreviewed;

    const markReviewed = useCallback(
      async ({
        returnToPreviewModeAfterSave,
        showSaveErrorToast,
      }: {
        returnToPreviewModeAfterSave: boolean;
        showSaveErrorToast: boolean;
      }): Promise<AnnotatedImageViewerSaveStatus> => {
        if (!canMarkReviewed || !onMarkReviewed) {
          if (returnToPreviewModeAfterSave) {
            setViewerEditMode(false);
          }
          return "no_changes";
        }

        setIsSaving(true);
        try {
          await onMarkReviewed();
          resetUnsavedChangeSources();
          if (returnToPreviewModeAfterSave) {
            setViewerEditMode(false);
          }
          return "saved";
        } catch (error) {
          if (showSaveErrorToast) {
            toast({
              title: "Save failed",
              description: "Failed to mark this result as reviewed. Please try again.",
              variant: "destructive",
            });
          }
          return "error";
        } finally {
          setIsSaving(false);
        }
      },
      [canMarkReviewed, onMarkReviewed, resetUnsavedChangeSources, setViewerEditMode, toast]
    );

    const markUnreviewed = useCallback(async () => {
      if (!canMarkUnreviewed || !onMarkUnreviewed) return;

      setIsSaving(true);
      try {
        await onMarkUnreviewed();
      } catch (error) {
        toast({
          title: "Save failed",
          description: "Failed to mark this result as unreviewed. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    }, [canMarkUnreviewed, onMarkUnreviewed, toast]);

    const saveAnnotations = useCallback(
      async ({
        returnToPreviewModeAfterSave,
        showSaveErrorToast,
      }: {
        returnToPreviewModeAfterSave: boolean;
        showSaveErrorToast: boolean;
      }): Promise<AnnotatedImageViewerSaveStatus> => {
        // Persisted filters deliberately rewrite annotation visibility on each
        // image. Outside-boundary detections now enter this same working state as
        // soon as they are shown, so every human edit follows one save path.
        const userDidOverride = annotationChangesRef.current || hasSidecarWriteAwaitingAggregateRef.current;
        if (!annotationChangesRef.current && !hasSidecarWriteAwaitingAggregateRef.current) {
          if (isEditMode && isReviewableUnreviewed) {
            return markReviewed({ returnToPreviewModeAfterSave, showSaveErrorToast });
          }
          if (returnToPreviewModeAfterSave) {
            setViewerEditMode(false);
          }
          return "no_changes";
        }

        // Route props change synchronously, while Storage metadata resolves
        // asynchronously. Never let A's retained dirty editor state close over B's
        // metadata/entry paths during that transition. A clean editor needs no
        // annotation snapshot and may still mark a deferred result reviewed above.
        if (!isAnnotationEditorCurrent) {
          if (showSaveErrorToast) {
            toast({
              title: "Save unavailable",
              description: "Wait for this image's annotations to finish loading.",
              variant: "destructive",
            });
          }
          return "error";
        }

        if (!metadataPath || !metadata || !metadata.metadata.annotations) {
          if (showSaveErrorToast) {
            toast({
              title: "Save failed",
              description: "Failed to save annotations. Please try again.",
              variant: "destructive",
            });
          }
          return "error";
        }

        const isKeypointAnnotation = extractedAnnotations?.annotationType === ANNOTATION_TYPES.KEY_POINTS;
        const isSegmentationAnnotation = extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION;
        const annotationChangeVersionAtSaveStart = annotationChangeVersionRef.current;

        setIsSaving(true);
        try {
          if ((isKeypointAnnotation || isSegmentationAnnotation) && !entryPath) {
            throw new Error(MISSING_ENTRY_PATH_ERROR);
          }

          if (isKeypointAnnotation) {
            const originalAnnotations = getLastSavedAnnotations();
            const category = originalAnnotations[0]?.category?.name ?? "plant";
            const currentKeypointAnnotations = parsedKeypointAnnotations;
            const keypointName =
              originalAnnotations.length === 0 ? "plant_center" : resolveKeypointName(originalAnnotations);
            const keypointSaveState = buildKeypointAnnotationSaveState(
              currentKeypointAnnotations,
              category,
              keypointName,
              originalAnnotations
            );
            const mutationBatches = buildKeypointAnnotationMutationBatches(keypointSaveState);
            const { modelKeypoints, manualKeypoints, deletedManualKeypointIds } = keypointSaveState;

            const fn = httpsCallable<
              {
                metadataPath: string;
                entryPath: string;
                mutationBatches: KeypointAnnotationSaveState[];
              },
              { success: boolean; createdIds: string[] }
            >(functions, "saveKeypointAnnotations");

            await fn({
              metadataPath,
              entryPath,
              mutationBatches,
            });
            hasSidecarWriteAwaitingAggregateRef.current = true;

            const modelKeypointById = new Map(modelKeypoints.map((state) => [state.id, state]));
            const manualKeypointById = new Map(manualKeypoints.map((state) => [state.id, state]));
            const deletedManualIds = new Set(deletedManualKeypointIds);
            const existingIds = new Set(originalAnnotations.map(({ id }) => id));
            const updatedAnnotations = originalAnnotations
              .filter(({ id }) => !deletedManualIds.has(id))
              .map((annotation) => {
                const updatedModelKeypoint = modelKeypointById.get(annotation.id);
                if (updatedModelKeypoint) return applySavedModelKeypointState(annotation, updatedModelKeypoint);
                const updatedManualKeypoint = manualKeypointById.get(annotation.id);
                if (updatedManualKeypoint) return applySavedManualKeypointState(annotation, updatedManualKeypoint);
                return annotation;
              });
            for (const manualKeypoint of manualKeypoints) {
              if (existingIds.has(manualKeypoint.id)) continue;
              updatedAnnotations.push(
                applySavedManualKeypointState(
                  {
                    id: manualKeypoint.id,
                    category: { name: manualKeypoint.category },
                    keypoints: [],
                    numKeypoints: 0,
                    source: { humanAnnotated: {} },
                    userVisibility: { isVisible: true },
                    userAdjustment: { manuallyCreated: true },
                    createdAt: new Date().toISOString(),
                  },
                  manualKeypoint
                )
              );
            }

            const visibleCount = currentKeypointAnnotations.filter(
              (annotation) => annotation.keypoint.visibility !== 0 && annotation.userVisibility?.isVisible !== false
            ).length;
            const countPerSquareMeter = computeCountPerSquareMeter(
              visibleCount,
              metadata.metadata.annotations?.boundaryAreaSquareMeters
            );
            const countPerAcre =
              countPerSquareMeter !== undefined ? countPerSquareMeterToPerAcre(countPerSquareMeter) : undefined;

            // The callable has committed Storage. Advance the diff baseline now,
            // even though the paired Firestore aggregate may still fail. The
            // split-write flag keeps the editor dirty; this baseline makes a
            // later user revert write the inverse delta back to the sidecar.
            savedAnnotationsRef.current = updatedAnnotations;
            await onSave?.({
              countPerSquareMeter,
              countPerAcre,
              objectCount: visibleCount,
              ...(userDidOverride ? { userDidOverride: true } : {}),
            });
            hasSidecarWriteAwaitingAggregateRef.current = false;
          }

          if (isSegmentationAnnotation) {
            const currentAnnotations = segmentationAnnotations;
            const {
              modelVisibilities,
              manualBBoxAnnotations,
              measurementEdits,
              keypointPositionEdits,
              categoryChanges,
            } = buildSegmentationAnnotationSaveState(currentAnnotations, getLastSavedAnnotations(), {
              // A capture with no depth cannot recompute values, so its endpoint
              // moves travel as positions and the server clears the axis values.
              geometryOnlyMeasurementEdits: measurementEditing.kind === "2d",
            });

            const fn = httpsCallable<
              {
                metadataPath: string;
                entryPath: string;
              } & SegmentationAnnotationSaveState,
              { success: boolean; createdIds: string[] }
            >(functions, "saveSegmentationAnnotations");

            await fn({
              metadataPath,
              entryPath,
              modelVisibilities,
              manualBBoxAnnotations,
              measurementEdits,
              keypointPositionEdits,
              categoryChanges,
            });
            hasSidecarWriteAwaitingAggregateRef.current = true;

            const visibleAnnotations = getAnnotationsForSavedAggregates(currentAnnotations);
            const visibleCount = visibleAnnotations.length;
            const countPerSquareMeter = computeCountPerSquareMeter(
              visibleCount,
              metadata.metadata.annotations?.boundaryAreaSquareMeters
            );
            const countPerAcre =
              countPerSquareMeter !== undefined ? countPerSquareMeterToPerAcre(countPerSquareMeter) : undefined;

            const lengths = visibleAnnotations
              .map((annotation) => annotation.measurements?.lengthM)
              .filter((length): length is number => typeof length === "number");
            const widths = visibleAnnotations
              .map((annotation) => annotation.measurements?.widthM)
              .filter((width): width is number => typeof width === "number");
            const masses = visibleAnnotations
              .map((annotation) => annotation.measurements?.massG)
              .filter((mass): mass is number => typeof mass === "number");

            const avgLengthMeters =
              lengths.length > 0
                ? Math.round((lengths.reduce((sum, value) => sum + value, 0) / lengths.length) * 10000) / 10000
                : null;
            const avgWidthMeters =
              widths.length > 0
                ? Math.round((widths.reduce((sum, value) => sum + value, 0) / widths.length) * 10000) / 10000
                : null;
            const totalWeightGrams =
              masses.length > 0 ? Math.round(masses.reduce((sum, value) => sum + value, 0) * 100) / 100 : null;
            const additionalMeasurements =
              additionalMeasurementDescriptors.length > 0
                ? computeVisibleAdditionalMeasurementAverages(visibleAnnotations, additionalMeasurementDescriptors)
                : undefined;

            // Storage is already authoritative after the callable resolves. Keep
            // the editor dirty through the split-write flag, while comparing any
            // subsequent edits against the sidecar state that actually exists.
            savedAnnotationsRef.current = currentAnnotations;
            await onSave?.({
              countPerSquareMeter,
              countPerAcre,
              objectCount: visibleCount,
              avgLengthMeters,
              avgWidthMeters,
              totalWeightGrams,
              ...(additionalMeasurements ? { additionalMeasurements } : {}),
              ...(userDidOverride ? { userDidOverride: true } : {}),
            });
            hasSidecarWriteAwaitingAggregateRef.current = false;
          }

          const lastSavedAnnotations = getLastSavedAnnotations();
          const annotationsChangedDuringSave =
            annotationChangeVersionRef.current !== annotationChangeVersionAtSaveStart;
          annotationChangesRef.current =
            annotationsChangedDuringSave &&
            (isKeypointAnnotation
              ? haveParsedKeypointAnnotationsChangedSinceLastSave(
                  latestKeypointAnnotationsRef.current,
                  lastSavedAnnotations
                )
              : isSegmentationAnnotation
                ? haveSegmentationAnnotationsChangedSinceLastSave(
                    latestSegmentationAnnotationsRef.current,
                    lastSavedAnnotations
                  )
                : false);

          if (annotationChangesRef.current) {
            syncUnsavedChangesState();
            toast({
              title: "New edits still need saving",
              description: "Changes made while the save was in progress were kept. Save again before leaving.",
            });
            return "new_changes";
          } else {
            resetUnsavedChangeSources();
            if (returnToPreviewModeAfterSave) {
              setViewerEditMode(false);
            }
          }
          return "saved";
        } catch (error) {
          // The toast can only carry a sentence; the console keeps the whole
          // rejection so a reported failure is diagnosable without a repro.
          console.error("Failed to save annotations", { metadataPath, entryPath, error });
          if (showSaveErrorToast) {
            toast({
              title: "Save failed",
              description: getAnnotationSaveErrorMessage(error),
              variant: "destructive",
            });
          }
          return "error";
        } finally {
          setIsSaving(false);
        }
      },
      [
        measurementEditing.kind,
        entryPath,
        extractedAnnotations?.annotationType,
        getLastSavedAnnotations,
        isEditMode,
        isAnnotationEditorCurrent,
        isReviewableUnreviewed,
        markReviewed,
        metadata,
        metadataPath,
        onSave,
        additionalMeasurementDescriptors,
        parsedKeypointAnnotations,
        segmentationAnnotations,
        resetUnsavedChangeSources,
        setViewerEditMode,
        syncUnsavedChangesState,
        toast,
      ]
    );

    const getNewlyAcceptedOutsideBoundaryAnnotationCount = useCallback(() => {
      const currentAnnotations =
        extractedAnnotations?.annotationType === ANNOTATION_TYPES.KEY_POINTS
          ? parsedKeypointAnnotations
          : extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION
            ? segmentationAnnotations
            : [];
      const currentVisibleIds = new Set(
        currentAnnotations.filter(({ userVisibility }) => userVisibility?.isVisible !== false).map(({ id }) => id)
      );

      return getLastSavedAnnotations().filter(
        (annotation) => currentVisibleIds.has(annotation.id) && isAnnotationEligibleForOutsideBoundaryReveal(annotation)
      ).length;
    }, [
      extractedAnnotations?.annotationType,
      getLastSavedAnnotations,
      parsedKeypointAnnotations,
      segmentationAnnotations,
    ]);

    const confirmOutsideBoundaryAnnotationsBeforeSave = useCallback((): Promise<boolean> => {
      if (hasAcknowledgedOutsideBoundarySaveWarning()) return Promise.resolve(true);

      const count = getNewlyAcceptedOutsideBoundaryAnnotationCount();
      if (count === 0) return Promise.resolve(true);

      outsideBoundarySaveConfirmationResolverRef.current?.(false);
      return new Promise<boolean>((resolve) => {
        outsideBoundarySaveConfirmationResolverRef.current = resolve;
        setOutsideBoundarySaveConfirmationCount(count);
      });
    }, [getNewlyAcceptedOutsideBoundaryAnnotationCount]);

    useEffect(
      () => () => {
        const resolve = outsideBoundarySaveConfirmationResolverRef.current;
        outsideBoundarySaveConfirmationResolverRef.current = null;
        resolve?.(false);
      },
      []
    );

    const saveFromToolbar = useCallback(async () => {
      if (!(await confirmOutsideBoundaryAnnotationsBeforeSave())) return;
      await saveAnnotations({
        returnToPreviewModeAfterSave: true,
        showSaveErrorToast: true,
      });
    }, [confirmOutsideBoundaryAnnotationsBeforeSave, saveAnnotations]);

    React.useImperativeHandle(
      ref,
      () => ({
        confirmOutsideBoundaryAnnotationsBeforeSave,
        saveIfThereAreUnsavedChanges: () =>
          saveAnnotations({
            returnToPreviewModeAfterSave: false,
            showSaveErrorToast: false,
          }),
        // The child is absent for keypoint images and during the cross-plot loading window, which
        // is exactly when arrows should fall through to plain image navigation.
        stepReviewObject: (direction) => segmentationRef.current?.stepReviewObject(direction) ?? "inactive",
        canDiscardUnsavedChanges: () => !hasSidecarWriteAwaitingAggregateRef.current,
        discardUnsavedChanges: () => {
          if (hasSidecarWriteAwaitingAggregateRef.current) return false;
          if (extractedAnnotations?.annotationType === ANNOTATION_TYPES.KEY_POINTS) {
            const savedKeypoints = parseKeypointAnnotations(getLastSavedAnnotations());
            latestKeypointAnnotationsRef.current = savedKeypoints;
            setKeypointAnnotations(savedKeypoints);
          }
          if (extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION) {
            const savedSegmentations = getLastSavedAnnotations();
            latestSegmentationAnnotationsRef.current = savedSegmentations;
            setSegmentationAnnotations(savedSegmentations);
          }
          resetUnsavedChangeSources();
          return true;
        },
      }),
      [
        confirmOutsideBoundaryAnnotationsBeforeSave,
        extractedAnnotations?.annotationType,
        getLastSavedAnnotations,
        resetUnsavedChangeSources,
        saveAnnotations,
      ]
    );

    useEffect(() => {
      // A metadata refetch (depth Retry, updatedAt race) must never clobber
      // annotation edits the user has in flight. A sidecar revision written by
      // THIS save is different: until its paired
      // Firestore aggregate succeeds, accepting it would erase the only dirty
      // retry signal and leave the two stores silently divergent.
      if (annotationChangesRef.current || hasSidecarWriteAwaitingAggregateRef.current) return;
      if (extractedAnnotations && metadata?.metadata.annotations) {
        const savedAnnotations = metadata.metadata.annotations.annotations;
        savedAnnotationsRef.current = savedAnnotations;

        if (extractedAnnotations.annotationType === ANNOTATION_TYPES.KEY_POINTS) {
          const parsedAnnotations = parseKeypointAnnotations(extractedAnnotations.list);
          latestKeypointAnnotationsRef.current = parsedAnnotations;
          setKeypointAnnotations(parsedAnnotations);
        }

        if (extractedAnnotations.annotationType === ANNOTATION_TYPES.SEGMENTATION) {
          latestSegmentationAnnotationsRef.current = extractedAnnotations.list;
          setSegmentationAnnotations(extractedAnnotations.list);
        }
        setAnnotationEditorContextKey(requestedEditorContextKey);
      }
    }, [extractedAnnotations, metadata, requestedEditorContextKey]);

    return (
      <div className="flex flex-col h-full bg-gray-100">
        {/* Top toolbar - with extra padding on right for the X close button */}
        <div className="flex items-center justify-between p-3 pr-12 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            {dataCollectionOnly && (
              <span className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600">
                Unannotated capture
              </span>
            )}
            {/* Annotation toggle - only show if annotations are available */}
            {hasAnnotations && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAnnotations}
                className="flex items-center gap-2">
                {showingAnnotations ? (
                  <>
                    <Eye className="h-4 w-4" />
                    <span className="text-sm">Hide Annotations</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4" />
                    <span className="text-sm">Show Annotations</span>
                  </>
                )}
              </Button>
            )}

            {/* Edit/review button — shown when NOT in edit mode, hidden if metadata loaded and annotation type is Unknown */}
            {hasAnnotations &&
              isEditable &&
              extractedAnnotations?.annotationType !== ANNOTATION_TYPES.UNKNOWN &&
              !isEditMode &&
              (canEdit ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewerEditMode(true)}
                  className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  <span className="text-sm">{isReviewableUnreviewed ? "Review" : "Edit"}</span>
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled
                          className="flex items-center gap-2 opacity-50">
                          <Pencil className="h-4 w-4" />
                          <span className="text-sm">{isReviewableUnreviewed ? "Review" : "Edit"}</span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>You can only edit annotations you collected</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}

            {canMarkReviewed && !isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void markReviewed({
                    returnToPreviewModeAfterSave: false,
                    showSaveErrorToast: true,
                  })
                }
                disabled={isSaving}
                className="flex items-center gap-2">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                <span className="text-sm">{isSaving ? "Saving..." : "Mark As Reviewed"}</span>
              </Button>
            )}

            {canMarkUnreviewed && !isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void markUnreviewed()}
                disabled={isSaving}
                className="flex items-center gap-2">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                <span className="text-sm">{isSaving ? "Saving..." : "Mark As Unreviewed"}</span>
              </Button>
            )}

            {/* Text toggle - only show if annotations are visible AND there are text labels to hide */}
            {showingAnnotations && hasTextLabels && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleText}
                className="flex items-center gap-2">
                {displayOptions.showText ? (
                  <>
                    <Type className="h-4 w-4" />
                    <span className="text-sm">Hide Text</span>
                  </>
                ) : (
                  <>
                    <RemoveFormatting className="h-4 w-4" />
                    <span className="text-sm">Show Text</span>
                  </>
                )}
              </Button>
            )}

            {/* Sweep every detection one at a time. Mirrors the gate on Segmentation's own
                `reviewActive`; the trailing `|| reviewObjectsMode` keeps an already-on toggle
                reachable on an image that happens to have no boxes, so it can be turned off. */}
            {isEditMode && isSegmentation && showingAnnotations && (hasBoundingBoxes || reviewObjectsMode) && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReviewObjectsModeChange(!reviewObjectsMode)}
                  className="flex items-center gap-2">
                  <ScanSearch className="h-4 w-4" />
                  <span className="text-sm">{reviewObjectsMode ? "Exit Review" : "Review Objects"}</span>
                </Button>
                {reviewObjectsMode && reviewStatus && (
                  <span
                    className="text-sm tabular-nums text-gray-600"
                    aria-live="polite">
                    {describeReviewStatus(reviewStatus)}
                  </span>
                )}
              </>
            )}

            {/* Debug: mirror the depth map + mapped endpoints (measurement captures only) */}
            {isEditMode && isSegmentation && depthAvailability?.status === "ready" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDepthDebug((value) => !value)}
                className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                <span className="text-sm">{getDepthToggleLabel(showDepthDebug)}</span>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasAnnotations &&
              isEditable &&
              extractedAnnotations?.annotationType !== ANNOTATION_TYPES.UNKNOWN &&
              isEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={saveFromToolbar}
                  disabled={isSaving || !isAnnotationEditorCurrent}
                  className="flex items-center gap-2">
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="text-sm">{isSaving ? "Saving..." : "Save"}</span>
                </Button>
              )}

            {/* Download button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownload}
                      disabled={isDownloading || isEditMode}
                      className="flex items-center gap-2">
                      {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      <span className="text-sm">Download</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                {isEditMode && <TooltipContent>Save changes before downloading</TooltipContent>}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Image container with zoom/pan */}
        {!dataCollectionOnly && isAnnotationEditorCurrent &&
          metadata &&
          extractedAnnotations?.annotationType === ANNOTATION_TYPES.KEY_POINTS && (
            <KeyPoints
              isEditMode={isEditMode}
              displayAnnotations={showingAnnotations}
              imageUrl={metadata.imageUrl}
              editorContextKey={requestedEditorContextKey}
              annotations={
                extractedAnnotations as {
                  list: Annotation[];
                  measureBoundaryPixels?: Point[];
                  annotationType: AnnotationType;
                }
              }
              parsedKeypointAnnotations={parsedKeypointAnnotations}
              onKeypointAnnotationsChange={updateKeypointAnnotations}
              boundaryAreaSquareMeters={metadata.metadata.annotations?.boundaryAreaSquareMeters}
              orientation={metadata.metadata.image.orientation}
              rawWidth={metadata.metadata.image.width}
              rawHeight={metadata.metadata.image.height}
              initialConfidenceThreshold={initialViewSettings?.keypointConfidenceThreshold}
              onConfidenceThresholdChange={handleKeypointConfidenceThresholdChange}
              initialPointSizePercent={initialViewSettings?.keypointSizePercent}
              onPointSizeChange={handleKeypointSizeChange}
              showOutsideBoundaryAnnotations={
                supportsOutsideBoundaryAnnotations ? showOutsideBoundaryAnnotations : false
              }
              onShowOutsideBoundaryAnnotationsChange={
                supportsOutsideBoundaryAnnotations ? handleShowOutsideBoundaryAnnotationsChange : undefined
              }
            />
          )}

        {!dataCollectionOnly && isAnnotationEditorCurrent &&
          metadata &&
          extractedAnnotations?.annotationType === ANNOTATION_TYPES.SEGMENTATION && (
            <Segmentation
              ref={segmentationRef}
              reviewObjectsMode={reviewObjectsMode}
              initialReviewEdge={initialReviewObjectEdge}
              onReviewStatusChange={handleReviewStatusChange}
              onExitReviewMode={handleExitReviewMode}
              key={annotationClassStateKey ?? resolvedAnnotationClassNames.join("|")}
              isEditMode={isEditMode}
              displayAnnotations={showingAnnotations}
              showText={displayOptions.showText}
              showMask={showMask}
              collapseToPoints={collapseBoxesToPoints}
              onCollapseToPointsChange={handleCollapseBoxesToPointsChange}
              imageUrl={metadata.imageUrl}
              editorContextKey={requestedEditorContextKey}
              annotations={{ ...extractedAnnotations!, list: segmentationAnnotations }}
              boundaryAreaSquareMeters={metadata.metadata.annotations?.boundaryAreaSquareMeters}
              orientation={metadata.metadata.image.orientation}
              rawWidth={metadata.metadata.image.width}
              rawHeight={metadata.metadata.image.height}
              containmentParams={containmentParams}
              measurementDescriptors={measurementDescriptors}
              annotationClassNames={resolvedAnnotationClassNames}
              annotationClassColorOrder={annotationClassColorOrder}
              annotationClassStateKey={annotationClassStateKey}
              depthAvailability={depthAvailability}
              onRetryDepthLoad={handleRetryDepthLoad}
              showDepthDebug={showDepthDebug}
              onCloseDepthDebug={() => setShowDepthDebug(false)}
              onSegmentationAnnotationsChange={updateSegmentationAnnotations}
              onShowMaskChange={handleShowMaskChange}
              initialFilterSettings={initialViewSettings?.segmentationFilters}
              initialHiddenMeasurementKeys={initialViewSettings?.hiddenMeasurementKeys}
              onFilterSettingsChange={handleFilterSettingsChange}
              onHiddenMeasurementKeysChange={handleHiddenMeasurementKeysChange}
              showOutsideBoundaryAnnotations={
                supportsOutsideBoundaryAnnotations ? showOutsideBoundaryAnnotations : false
              }
              onShowOutsideBoundaryAnnotationsChange={
                supportsOutsideBoundaryAnnotations ? handleShowOutsideBoundaryAnnotationsChange : undefined
              }
            />
          )}

        {(dataCollectionOnly || !isAnnotationEditorCurrent ||
          !metadata ||
          !extractedAnnotations ||
          extractedAnnotations.annotationType === ANNOTATION_TYPES.UNKNOWN) && (
          <div
            ref={containerRef}
            className="flex-1 overflow-hidden flex items-center justify-center bg-gray-100"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={handleDoubleClick}
            style={{
              cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            }}>
            {isLoading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Loading annotations...</span>
              </div>
            ) : (
              <div
                ref={imageRef}
                className="transition-transform duration-100"
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                }}>
                {/* A first-load metadata failure never mounts the annotation editor,
                    so its retry pill can't render — surface the retry here instead. */}
                {metadataLoadFailed && !metadata && metadataPath && (
                  <div className="absolute left-2 top-2 z-10 flex max-w-sm items-center gap-2 rounded bg-red-700/90 px-2 py-1 text-xs text-white">
                    <span>Couldn't load capture info</span>
                    <button
                      type="button"
                      onClick={reloadCaptureMetadata}
                      className="shrink-0 rounded bg-white/20 px-1.5 py-0.5 font-medium hover:bg-white/30">
                      Retry
                    </button>
                  </div>
                )}
                {showingAnnotations && annotatedImageUrl ? (
                  <img
                    src={annotatedImageUrl}
                    alt="Annotated"
                    className="max-w-full max-h-[75vh] object-contain pointer-events-none select-none"
                    draggable={false}
                  />
                ) : originalImageUrl ? (
                  <img
                    src={originalImageUrl}
                    alt="Original"
                    className="max-w-full max-h-[75vh] object-contain pointer-events-none select-none"
                    draggable={false}
                  />
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* Zoom indicator */}
        {scale > 1 && (
          <div className="absolute bottom-4 left-4 px-2 py-1 bg-black/50 text-white text-xs rounded">
            {Math.round(scale * 100)}% - Double-click to reset
          </div>
        )}

        <AlertDialog
          open={outsideBoundarySaveConfirmationCount !== null}
          onOpenChange={(open) => {
            if (!open) resolveOutsideBoundarySaveConfirmation(false);
          }}>
          <AlertDialogContent className="w-[calc(100%-2rem)] sm:max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Save annotations outside the measurement area?</AlertDialogTitle>
              <AlertDialogDescription>
                {outsideBoundarySaveConfirmationCount ?? 0} outside-boundary{" "}
                {outsideBoundarySaveConfirmationCount === 1 ? "annotation is" : "annotations are"} already included in
                the displayed count and density. Saving will also include{" "}
                {outsideBoundarySaveConfirmationCount === 1 ? "it" : "them"} in exports and training data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:space-x-0">
              <AlertDialogCancel
                className="w-full sm:w-auto"
                disabled={isSaving}>
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                className="w-full sm:w-auto"
                disabled={isSaving}
                onClick={() => resolveOutsideBoundarySaveConfirmation(true)}>
                Save {outsideBoundarySaveConfirmationCount ?? 0} outside{" "}
                {outsideBoundarySaveConfirmationCount === 1 ? "annotation" : "annotations"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }
);

AnnotatedImageViewer.displayName = "AnnotatedImageViewer";

export default AnnotatedImageViewer;
